import type {LoaderFunctionArgs} from 'react-router'

import type {AppThunk} from '../../../actions/types'
import {getSingleBuildArg} from '../../../rest/builds'
import {getBuildTypeArg} from '../../../rest/buildTypes'
import {getBuildLocator} from '../../../rest/locators'
import {loadEndpoint} from '../../../routes/loadEndpoint'
import type {LoadedEndpoints} from '../../../routes/loadEndpoint'
import {restApi} from '../../../services/rest'
import {BuildPageTabNamesEnum, toBuildId, toBuildTypeId} from '../../../types'
import {parseBranch} from '../../../utils/branchNames'
import {getPermalinkLocator} from '../../../utils/permalinks'
import {queryToObject} from '../../../utils/queryParams'
import {preloadBuildTypeInvestigationsBadge} from '../../common/Badges/BuildTypeInvestigationsBadge/BuildTypeInvestigationsBadge.preload'
import {preloadSkipAssignInvestigation} from '../../common/Investigations/Investigations.hooks.preload'

import {preloadBuildTabs} from './BuildTabs/BuildTabs.preload'

export const preloadBuildPage =
  ({params, request}: LoaderFunctionArgs): AppThunk<LoadedEndpoints> =>
  dispatch => {
    const url = new URL(request.url)
    const queryParams = queryToObject(url.search)
    const {buildTab = BuildPageTabNamesEnum.OVERVIEW, branch} = queryParams

    const buildTypeId = toBuildTypeId(params.buildTypeId ?? '')

    const permalinkLocator = getPermalinkLocator(
      params.buildId ?? '',
      buildTypeId,
      parseBranch(branch),
    )

    const preloadedEndpoints: LoadedEndpoints = []
    if (permalinkLocator != null) {
      preloadedEndpoints.push(
        dispatch(
          loadEndpoint(restApi.endpoints.getBuild, {
            buildLocator: permalinkLocator,
            fields: 'id',
            essential: true,
          }),
        ),
      )
    } else {
      const buildId = toBuildId(params.buildId ?? 0)
      preloadedEndpoints.push(
        ...dispatch(preloadBuildTabs(buildId, buildTab, queryParams)),
        dispatch(
          loadEndpoint(
            restApi.endpoints.getBuildNormalizedAsList,
            getSingleBuildArg(getBuildLocator(buildId!), {
              withBuildTypeDetails: true,
            }),
          ),
        ),
      )
    }

    if (buildTypeId) {
      preloadedEndpoints.push(
        dispatch(
          loadEndpoint(restApi.endpoints.getBuildTypeNormalized, getBuildTypeArg(buildTypeId)),
        ),
        ...dispatch(preloadSkipAssignInvestigation(buildTypeId)),
        ...dispatch(preloadBuildTypeInvestigationsBadge(buildTypeId)),
      )
    }

    return preloadedEndpoints
  }
